import { User } from './types'

export type DirectorySurface = 'mail' | 'calendar' | 'contacts' | 'drive'

interface DirectoryEntry {
  kind?: string
  id?: string
  displayName?: string
  name?: string
  email?: string
  fullname?: string
  login?: string
  phones?: string[]
  groups?: string[]
  avatarUrl?: string
  openpaasId?: string
  userId?: string
  memberCount?: number
  eligibleMemberCount?: number
  selectable?: boolean
  disabledReason?: string | null
  _type?: string
}

const directoryUserKey = (entry: DirectoryEntry): string =>
  String(entry.id || entry.email || entry.displayName || '').trim()

export const directoryEntryToUser = (entry: DirectoryEntry): User => {
  const kind = entry.kind === 'group' ? 'group' : 'user'
  if (kind === 'group') {
    return {
      kind,
      objectType: 'group',
      directoryId: directoryUserKey(entry),
      email: '',
      displayName: entry.displayName || entry.name || '',
      memberCount: entry.memberCount,
      eligibleMemberCount: entry.eligibleMemberCount,
      selectable: entry.selectable,
      disabledReason: entry.disabledReason
    }
  }
  const email = String(entry.email || '').trim().toLowerCase()
  return {
    kind,
    objectType: 'user',
    directoryId: directoryUserKey(entry),
    email,
    displayName: entry.displayName || entry.fullname || email,
    avatarUrl: entry.avatarUrl || '',
    ...(entry.openpaasId || entry.userId
      ? { openpaasId: entry.openpaasId || entry.userId }
      : {}),
    login: entry.login,
    phones: entry.phones,
    groups: entry.groups
  }
}

const readDirectoryPayload = async (
  response: Response
): Promise<DirectoryEntry[]> => {
  const payload = await response.json().catch(() => ({}))
  if (!response.ok || !payload?.ok) {
    throw new Error(payload?.error || `directory_http_${response.status}`)
  }
  return Array.isArray(payload.results) ? payload.results : []
}

export async function searchCorporateDirectory(
  query: string,
  surface: DirectorySurface
): Promise<User[]> {
  const params = new URLSearchParams({
    q: query,
    limit: query.trim() ? '20' : '5',
    surface
  })
  const response = await fetch(`/__engpro/directory?${params.toString()}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store'
  })
  const entries = await readDirectoryPayload(response)
  return entries
    .filter(entry => entry.selectable !== false)
    .map(directoryEntryToUser)
}

export async function resolveCorporateDirectorySelections(
  selections: Array<{ kind: 'user' | 'group'; id: string }>,
  surface: DirectorySurface
): Promise<User[]> {
  const params = new URLSearchParams({ surface })
  const response = await fetch(
    `/__engpro/directory/resolve?${params.toString()}`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: 'same-origin',
      cache: 'no-store',
      body: JSON.stringify({ selections })
    }
  )
  const payload = await response.json().catch(() => ({}))
  if (!response.ok || !payload?.ok) {
    throw new Error(
      payload?.error || `directory_resolve_http_${response.status}`
    )
  }
  return (Array.isArray(payload.recipients) ? payload.recipients : [])
    .map(directoryEntryToUser)
    .filter(user => Boolean(user.email))
}
